import React from 'react';
import { FormAttribution, formAttribution } from '../../utilities/attribution';

// #region useEmbed
/**
 * `?embed=1` turns the booking page into a widget for another site: no page
 * chrome, a transparent ground, and the host is told the content height on every
 * change so its iframe never shows a scrollbar. Only the height leaves the frame.
 */
export const useEmbed = (params: URLSearchParams): boolean => {
    const embedded = params.get('embed') === '1' && window.parent !== window;

    React.useEffect(() => {
        if (!embedded) return;
        document.documentElement.style.background = 'transparent';
        document.body.style.background = 'transparent';
        // Measure the widget card, not the document: the app root is full-height, so the document never shrinks or grows with the steps.
        let last = 0;
        const target = (): HTMLElement => document.querySelector<HTMLElement>('[data-embed-root]') ?? document.body;
        const send = () => {
            const height = Math.ceil(target().getBoundingClientRect().height);
            if (!height || height === last) return;
            last = height;
            window.parent.postMessage({ type: 'wallmates:resize', height }, '*');
        };
        const observer = new ResizeObserver(send);
        // The card mounts after the first render; watch for it and re-attach if a state change replaces it.
        let watched: HTMLElement | null = null;
        const attach = () => {
            const el = target();
            if (el === watched) return;
            if (watched) observer.unobserve(watched);
            watched = el;
            observer.observe(el);
            send();
        };
        const mutations = new MutationObserver(attach);
        mutations.observe(document.body, { childList: true, subtree: true });
        attach();
        return () => { observer.disconnect(); mutations.disconnect(); };
    }, [embedded]);

    return embedded;
};

/**
 * Where a booking made without an enquiry came from: the tags, click ids and entry point the host passed on
 * the iframe address, plus the visitor's remembered first and latest touch (see utilities/attribution).
 */
export const attributionFromQuery = (params: URLSearchParams): FormAttribution => formAttribution(params, 'booking_page');
